import { useState } from 'react';
import { Link } from 'react-router-dom';

const BEAST_IMG = 'https://storage.readdy-site.link/project_files/b51ed5a1-af1a-4207-b52d-ef48f1af5b2c/240680cf-4f65-4a74-9aeb-9bf49352638a_unnamed.jpeg?v=77b6cbd8e317391be7ec7f525a3025c7';

interface Panel {
  bgColor: string;
  speaker: 'beast' | 'patient' | 'both' | 'narration';
  beastMood?: 'normal' | 'excited' | 'serious' | 'shocked' | 'happy' | 'thinking';
  patientMood?: 'neutral' | 'embarrassed' | 'happy' | 'skeptical' | 'relieved';
  speechBubble?: string;
  patientSpeech?: string;
  narration?: string;
  sfx?: string;
  prop?: string;
  propIcon?: string;
}

interface Episode {
  number: number;
  title: string;
  subtitle: string;
  accentColor: string;
  headerBg: string;
  panels: Panel[];
}

const episodes: Episode[] = [
  {
    number: 1,
    title: '監査の神様',
    subtitle: '処方箋を見て疑義照会するあるある',
    accentColor: '#dc2626',
    headerBg: 'linear-gradient(135deg, #1e1e2e 0%, #2d1b4e 100%)',
    panels: [
      {
        bgColor: '#fef9c3',
        speaker: 'beast',
        beastMood: 'excited',
        speechBubble: '今日も張り切って\n監査するぞ！',
        sfx: 'ファイトー!!',
        prop: '処方箋の束',
        propIcon: 'ri-file-list-3-line',
      },
      {
        bgColor: '#dbeafe',
        speaker: 'beast',
        beastMood: 'thinking',
        speechBubble: 'ん…？この\n処方箋…',
        narration: '数十枚目…',
        sfx: 'じっ…',
        prop: '処方箋',
        propIcon: 'ri-file-paper-2-line',
      },
      {
        bgColor: '#fee2e2',
        speaker: 'beast',
        beastMood: 'shocked',
        speechBubble: 'この用量、\nおかしくないか！？',
        sfx: 'ハッ!!',
        prop: '処方箋（アップ）',
        propIcon: 'ri-zoom-in-line',
      },
      {
        bgColor: '#dcfce7',
        speaker: 'beast',
        beastMood: 'serious',
        speechBubble: '先生、疑義照会\nさせてください！\n（また見つけた…）',
        sfx: 'プルルル…',
        prop: '電話',
        propIcon: 'ri-phone-line',
      },
    ],
  },
  {
    number: 2,
    title: 'お薬手帳の重要性',
    subtitle: '忘れてもアプリで解決する流れ',
    accentColor: '#0891b2',
    headerBg: 'linear-gradient(135deg, #0c4a6e 0%, #164e63 100%)',
    panels: [
      {
        bgColor: '#f0fdf4',
        speaker: 'beast',
        beastMood: 'normal',
        speechBubble: 'お薬手帳は\nお持ちですか？',
        prop: 'お薬手帳',
        propIcon: 'ri-book-2-line',
      },
      {
        bgColor: '#fff7ed',
        speaker: 'patient',
        patientMood: 'embarrassed',
        patientSpeech: 'あっ…\n忘れてきました\nすみません…',
        sfx: 'しゅん…',
        prop: '空の手',
        propIcon: 'ri-hand-coin-line',
      },
      {
        bgColor: '#eff6ff',
        speaker: 'beast',
        beastMood: 'happy',
        speechBubble: '大丈夫ですよ！\nスマホのアプリでも\n確認できます！',
        sfx: 'キラーン☆',
        prop: 'スマートフォン',
        propIcon: 'ri-smartphone-line',
      },
      {
        bgColor: '#fdf4ff',
        speaker: 'patient',
        patientMood: 'happy',
        patientSpeech: '便利！次回から\n絶対使います！\nありがとう！',
        sfx: 'ぱあっ！',
        prop: 'アプリ画面',
        propIcon: 'ri-apps-line',
      },
    ],
  },
  {
    number: 3,
    title: 'ジェネリックの説明',
    subtitle: 'ジェネリックを勧める定番シーン',
    accentColor: '#059669',
    headerBg: 'linear-gradient(135deg, #064e3b 0%, #065f46 100%)',
    panels: [
      {
        bgColor: '#ecfdf5',
        speaker: 'beast',
        beastMood: 'normal',
        speechBubble: 'こちら、ジェネリック\n医薬品に変更\nできますよ',
        prop: '薬の箱2種',
        propIcon: 'ri-medicine-bottle-line',
      },
      {
        bgColor: '#fefce8',
        speaker: 'patient',
        patientMood: 'skeptical',
        patientSpeech: '効き目って\n本当に同じ\nなんですか…？',
        sfx: 'ジーっ',
        prop: '薬の箱',
        propIcon: 'ri-question-line',
      },
      {
        bgColor: '#eff6ff',
        speaker: 'beast',
        beastMood: 'serious',
        speechBubble: '主成分は\n全く同じです！\n国が認めた薬ですよ',
        sfx: 'ズバッ！',
        prop: '承認書類',
        propIcon: 'ri-government-line',
      },
      {
        bgColor: '#f0fdf4',
        speaker: 'both',
        beastMood: 'happy',
        patientMood: 'happy',
        speechBubble: '安心しました！\nぜひお願いします！',
        patientSpeech: 'お財布にも\n優しくなりますよ！',
        sfx: '✨ WIN-WIN ✨',
        prop: '笑顔',
        propIcon: 'ri-emotion-happy-line',
      },
    ],
  },
];

function PatientCharacter({ mood }: { mood?: string }) {
  const color =
    mood === 'embarrassed'
      ? 'text-orange-400'
      : mood === 'happy'
      ? 'text-yellow-400'
      : mood === 'skeptical'
      ? 'text-gray-500'
      : 'text-blue-400';
  const icon =
    mood === 'embarrassed'
      ? 'ri-emotion-unhappy-line'
      : mood === 'happy'
      ? 'ri-emotion-laugh-line'
      : mood === 'skeptical'
      ? 'ri-emotion-normal-line'
      : 'ri-user-smile-line';
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="w-14 h-14 rounded-full bg-white border-3 border-gray-800 flex items-center justify-center shadow-sm"
        style={{ border: '3px solid #1f2937' }}>
        <i className={`${icon} ${color} text-3xl`}></i>
      </div>
      <div className="text-xs text-gray-500 font-bold">患者さん</div>
    </div>
  );
}

function BeastCharacter({ mood, small }: { mood?: string; small?: boolean }) {
  const size = small ? 'w-12 h-12' : 'w-16 h-16';
  const filter =
    mood === 'excited'
      ? 'brightness(1.05)'
      : mood === 'shocked'
      ? 'brightness(0.95) contrast(1.1)'
      : mood === 'serious'
      ? 'contrast(1.05)'
      : mood === 'happy'
      ? 'brightness(1.08) saturate(1.1)'
      : 'none';
  const transform =
    mood === 'excited'
      ? 'scale(1.1) rotate(-3deg)'
      : mood === 'shocked'
      ? 'scale(1.12) rotate(2deg)'
      : mood === 'thinking'
      ? 'scale(1.0) rotate(-1deg)'
      : 'scale(1.0)';
  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className={`${size} rounded-full overflow-hidden border-gray-800`}
        style={{ border: '3px solid #1f2937', transform, filter }}
      >
        <img
          src={BEAST_IMG}
          alt="ビーストくん"
          className="w-full h-full object-cover object-top"
        />
      </div>
      {!small && <div className="text-xs text-gray-500 font-bold">ビーストくん</div>}
    </div>
  );
}

function SpeechBubble({
  text,
  direction = 'left',
  color = 'white',
  small,
}: {
  text: string;
  direction?: 'left' | 'right';
  color?: string;
  small?: boolean;
}) {
  return (
    <div className="relative">
      <div
        className={`${small ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-xs'} font-bold text-gray-900 rounded-xl leading-tight whitespace-pre-line text-center`}
        style={{
          background: color,
          border: '2.5px solid #1f2937',
          maxWidth: '130px',
          lineHeight: '1.4',
        }}
      >
        {text}
      </div>
      <div
        className="absolute"
        style={{
          bottom: '-8px',
          [direction === 'left' ? 'left' : 'right']: '18px',
          width: 0,
          height: 0,
          borderLeft: '7px solid transparent',
          borderRight: '7px solid transparent',
          borderTop: '8px solid #1f2937',
        }}
      />
      <div
        className="absolute"
        style={{
          bottom: '-5px',
          [direction === 'left' ? 'left' : 'right']: '19px',
          width: 0,
          height: 0,
          borderLeft: '6px solid transparent',
          borderRight: '6px solid transparent',
          borderTop: `7px solid ${color}`,
        }}
      />
    </div>
  );
}

function MangaPanel({ panel, panelIndex }: { panel: Panel; panelIndex: number }) {
  const isLast = panelIndex === 3;
  return (
    <div
      className="relative flex flex-col items-center justify-between p-3 gap-2"
      style={{
        background: panel.bgColor,
        minHeight: '180px',
        borderBottom: isLast ? 'none' : '3px solid #1f2937',
        position: 'relative',
      }}
    >
      {/* Panel number */}
      <div
        className="absolute top-2 left-2 w-6 h-6 flex items-center justify-center text-xs font-black text-white z-10"
        style={{ background: '#1f2937', borderRadius: '50%' }}
      >
        {panelIndex + 1}
      </div>

      {/* SFX */}
      {panel.sfx && (
        <div
          className="absolute top-2 right-2 text-xs font-black z-10"
          style={{ color: '#dc2626', fontFamily: '"Noto Sans JP", sans-serif', transform: 'rotate(3deg)' }}
        >
          {panel.sfx}
        </div>
      )}

      {/* Narration */}
      {panel.narration && (
        <div
          className="absolute top-8 left-0 right-0 text-center text-xs text-gray-500 italic z-10 px-2"
          style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
        >
          {panel.narration}
        </div>
      )}

      {/* Main Content */}
      <div className="flex flex-col items-center justify-center gap-2 flex-1 pt-4">
        {panel.speaker === 'beast' && (
          <>
            {panel.speechBubble && (
              <SpeechBubble text={panel.speechBubble} direction="left" />
            )}
            <div className="flex items-end gap-3 mt-1">
              <BeastCharacter mood={panel.beastMood} />
              {panel.propIcon && (
                <div
                  className="w-10 h-10 flex items-center justify-center rounded-lg"
                  style={{ background: 'rgba(255,255,255,0.8)', border: '2px solid #6b7280' }}
                >
                  <i className={`${panel.propIcon} text-xl text-gray-600`}></i>
                </div>
              )}
            </div>
          </>
        )}

        {panel.speaker === 'patient' && (
          <>
            {panel.patientSpeech && (
              <SpeechBubble text={panel.patientSpeech} direction="right" color="#fef9c3" />
            )}
            <div className="flex items-end gap-3 mt-1">
              <PatientCharacter mood={panel.patientMood} />
              {panel.propIcon && (
                <div
                  className="w-10 h-10 flex items-center justify-center rounded-lg"
                  style={{ background: 'rgba(255,255,255,0.8)', border: '2px solid #6b7280' }}
                >
                  <i className={`${panel.propIcon} text-xl text-gray-600`}></i>
                </div>
              )}
            </div>
          </>
        )}

        {panel.speaker === 'both' && (
          <div className="flex flex-col items-center gap-2 w-full">
            <div className="flex justify-between w-full px-1">
              {panel.patientSpeech && (
                <div className="flex-1 flex justify-start">
                  <SpeechBubble text={panel.patientSpeech} direction="left" color="#fef9c3" small />
                </div>
              )}
              {panel.speechBubble && (
                <div className="flex-1 flex justify-end">
                  <SpeechBubble text={panel.speechBubble} direction="right" small />
                </div>
              )}
            </div>
            <div className="flex items-end justify-center gap-6">
              <PatientCharacter mood={panel.patientMood} />
              <BeastCharacter mood={panel.beastMood} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function EpisodeCard({ episode }: { episode: Episode }) {
  return (
    <div className="w-full max-w-[260px]">
      {/* Episode Header */}
      <div
        className="text-white text-center py-3 px-4"
        style={{ background: episode.headerBg, borderRadius: '8px 8px 0 0' }}
      >
        <div className="text-xs tracking-widest opacity-70 mb-1">EPISODE {episode.number}</div>
        <div className="font-black text-base tracking-tight leading-tight">
          「{episode.title}」
        </div>
        <div className="text-xs opacity-70 mt-1">{episode.subtitle}</div>
      </div>

      {/* Manga Panels */}
      <div
        className="overflow-hidden"
        style={{
          border: '4px solid #1f2937',
          borderTop: 'none',
          borderRadius: '0 0 8px 8px',
          background: '#fff',
        }}
      >
        {episode.panels.map((panel, idx) => (
          <MangaPanel key={idx} panel={panel} panelIndex={idx} />
        ))}
      </div>

      {/* Episode Footer */}
      <div className="text-center mt-2">
        <span
          className="text-xs font-bold px-3 py-1 rounded-full"
          style={{ background: episode.accentColor + '22', color: episode.accentColor }}
        >
          薬剤師あるある Vol.{episode.number}
        </span>
      </div>
    </div>
  );
}

function BeastProfile() {
  return (
    <div className="max-w-2xl mx-auto mb-12">
      <div
        className="relative overflow-hidden rounded-2xl"
        style={{ border: '3px solid #1f2937', background: '#fff' }}
      >
        {/* Header bg strip */}
        <div
          className="h-20 w-full"
          style={{ background: 'linear-gradient(135deg, #1e1e2e 0%, #3b1f6e 50%, #1e3a5f 100%)' }}
        />

        {/* Profile content */}
        <div className="px-6 pb-6 -mt-10 relative">
          <div className="flex flex-col sm:flex-row items-center sm:items-end gap-5">
            {/* Avatar */}
            <div
              className="w-24 h-24 rounded-full overflow-hidden flex-shrink-0"
              style={{ border: '4px solid #fff', boxShadow: '0 4px 12px rgba(0,0,0,0.18)' }}
            >
              <img
                src={BEAST_IMG}
                alt="ビーストくん"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Name & Title */}
            <div className="text-center sm:text-left pb-1">
              <div className="text-xs font-bold tracking-widest text-purple-600 uppercase mb-0.5">
                主人公キャラクター
              </div>
              <h4 className="text-2xl font-black text-gray-900 tracking-tight leading-tight">
                ビーストくん
              </h4>
              <div className="flex flex-wrap justify-center sm:justify-start gap-1.5 mt-2">
                <span
                  className="text-xs font-bold px-2.5 py-0.5 rounded-full"
                  style={{ background: '#7c3aed22', color: '#7c3aed' }}
                >
                  薬剤師
                </span>
                <span
                  className="text-xs font-bold px-2.5 py-0.5 rounded-full"
                  style={{ background: '#05966922', color: '#059669' }}
                >
                  疑義照会の神様
                </span>
                <span
                  className="text-xs font-bold px-2.5 py-0.5 rounded-full"
                  style={{ background: '#dc262622', color: '#dc2626' }}
                >
                  患者思い
                </span>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="mt-5 mb-4" style={{ borderTop: '2px dashed #e5e7eb' }} />

          {/* Stats grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            {[
              { icon: 'ri-hospital-line', label: '職場', value: '地域薬局' },
              { icon: 'ri-award-line', label: '経験', value: '10年以上' },
              { icon: 'ri-heart-pulse-line', label: '特技', value: '疑義照会' },
              { icon: 'ri-chat-smile-2-line', label: '好きなこと', value: '患者さんとの対話' },
            ].map((stat) => (
              <div
                key={stat.label}
                className="flex flex-col items-center text-center p-3 rounded-xl gap-1.5"
                style={{ background: '#f9fafb', border: '1.5px solid #e5e7eb' }}
              >
                <div
                  className="w-8 h-8 flex items-center justify-center rounded-full"
                  style={{ background: '#1e1e2e' }}
                >
                  <i className={`${stat.icon} text-white text-sm`}></i>
                </div>
                <div className="text-xs text-gray-400 font-medium">{stat.label}</div>
                <div className="text-xs font-black text-gray-800 leading-tight">{stat.value}</div>
              </div>
            ))}
          </div>

          {/* Bio */}
          <div
            className="rounded-xl p-4"
            style={{ background: '#f0fdf4', border: '1.5px solid #bbf7d0' }}
          >
            <div className="flex items-start gap-3">
              <div
                className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full mt-0.5"
                style={{ background: '#059669' }}
              >
                <i className="ri-user-voice-line text-white text-sm"></i>
              </div>
              <div>
                <div className="text-xs font-black text-green-800 mb-1">自己紹介</div>
                <p className="text-xs text-gray-700 leading-relaxed">
                  みなさんこんにちは、ビーストくんです！地域の薬局で毎日バリバリ働いています。
                  疑義照会は得意中の得意！患者さんの笑顔のために、今日もお薬の知識を全力で届けます。
                  薬剤師あるあるを四コマ漫画でゆる〜くお届けするので、ぜひ楽しんでください🎉
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Corner accent */}
        <div
          className="absolute top-4 right-4 text-white text-xs font-black tracking-widest opacity-60"
          style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
        >
          BEAST PHARMACIST
        </div>
      </div>
    </div>
  );
}

export default function FourPanelManga() {
  const [selectedEpisode, setSelectedEpisode] = useState<number | null>(null);

  return (
    <div className="mb-16">
      {/* Section Header */}
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-3 mb-3">
          <div className="h-0.5 w-12 bg-gray-800"></div>
          <div
            className="w-12 h-12 rounded-full overflow-hidden"
            style={{ border: '3px solid #1f2937' }}
          >
            <img src={BEAST_IMG} alt="ビーストくん" className="w-full h-full object-cover object-top" />
          </div>
          <div className="h-0.5 w-12 bg-gray-800"></div>
        </div>
        <h3 className="text-2xl font-black text-gray-900 tracking-tight">
          <Link
            to="/manga"
            className="hover:opacity-75 transition-opacity cursor-pointer inline-flex items-center gap-2 group"
          >
            薬剤師あるある <span className="text-purple-600">四コマ漫画</span>
            <i className="ri-external-link-line text-lg text-gray-400 group-hover:text-purple-600 transition-colors"></i>
          </Link>
        </h3>
        <p className="text-sm text-gray-500 mt-1">主人公：ビーストくん（薬剤師）</p>
      </div>

      {/* Beast Profile Card */}
      <BeastProfile />

      {/* Episode Selector (mobile) */}
      <div className="flex justify-center gap-2 mb-8 md:hidden">
        {episodes.map((ep) => (
          <button
            key={ep.number}
            onClick={() => setSelectedEpisode(selectedEpisode === ep.number ? null : ep.number)}
            className="px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap cursor-pointer transition-all"
            style={{
              background: selectedEpisode === ep.number ? ep.accentColor : '#f3f4f6',
              color: selectedEpisode === ep.number ? '#fff' : '#374151',
            }}
          >
            第{ep.number}話
          </button>
        ))}
      </div>

      {/* Desktop: All 3 episodes side by side */}
      <div className="hidden md:flex justify-center gap-10 flex-wrap">
        {episodes.map((ep) => (
          <EpisodeCard key={ep.number} episode={ep} />
        ))}
      </div>

      {/* Mobile: Show selected episode or all stacked */}
      <div className="md:hidden flex flex-col items-center gap-10">
        {episodes
          .filter((ep) => selectedEpisode === null || ep.number === selectedEpisode)
          .map((ep) => (
            <EpisodeCard key={ep.number} episode={ep} />
          ))}
      </div>

      {/* More Button */}
      <div className="text-center mt-10">
        <button
          className="font-bold py-3 px-10 rounded-full whitespace-nowrap cursor-pointer transition-all"
          style={{
            background: 'linear-gradient(135deg, #7c3aed 0%, #9333ea 100%)',
            color: '#fff',
            fontSize: '0.9rem',
          }}
        >
          <i className="ri-add-circle-line mr-2"></i>第4話以降を見る（準備中）
        </button>
      </div>
    </div>
  );
}
